import { useEffect, useState } from 'react';

import { useFormikContext } from 'formik';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { useZipCodeDeliverabilityCheck } from 'src/app/hooks/data/zipCode';
import { useOnboardingZipCodePersistentData } from 'src/app/hooks/feature_data/useOnboardingZipCodePersistentData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import { EmptyHeight, StyledSuccessMessage } from '../styles';

export const StyledFormikTextField = styled(FormikTextField)`
  & > label > span {
    font-size: 1rem;
  }
  & label {
    color: ${({ theme }) => theme.colors.capsuleGreen60};
    font-weight: 400;
  }
`;

const StyledIcon = styled(CapsuleIcon)`
  appearance: none;
  border: none;
  border-radius: ${({ theme }) => theme.radii.r100};
  background-color: ${({ theme }) => theme.colors.transparent};
  cursor: pointer;
  & > svg {
    height: 100% !important;
    width: 100% !important;
    * {
      fill: ${({ theme }) => theme.colors.capsuleGreen60};
    }
  }
`;

interface OnboardingZipCodeProps {
  name: string;
  defaultValue?: string;
  id?: string;
  label?: string;
  labelOnFocus?: string;
  wrapperClassName?: string;
  successMessage?: string;
  error?: string;
  disableDeliverabilityCheck?: boolean;
}

interface FormValues {
  [key: string]: string;
}

export const OnboardingZipCode = ({
  name,
  defaultValue,
  id,
  label,
  labelOnFocus,
  wrapperClassName,
  successMessage,
  disableDeliverabilityCheck = false,
}: OnboardingZipCodeProps) => {
  const { setOnboardingZipCodePersistentData, isLoading } = useOnboardingZipCodePersistentData();
  const trackZipCodeCheckSuccess = useSegmentEvent('deliveryAreaEligibilityZipcodeSuccessful', {
    shouldTrackOnMount: false,
  });
  const trackZipCodeCheckFailed = useSegmentEvent('deliveryAreaEligibilityZipcodeFailed', {
    shouldTrackOnMount: false,
  });
  const trackFieldFocused = useSegmentEvent('fieldFocused', {
    shouldTrackOnMount: false,
  });
  const { pathname } = useLocation();

  const defaultLabel = label ? label : 'Zip code (e.g. home or work)';
  const defaultLabelOnFocus = labelOnFocus ? labelOnFocus : 'Zip code';
  const [internalLabel, setInternalLabel] = useState(defaultLabel);

  const { values } = useFormikContext<FormValues>();
  const zipCode = values[name] || '';
  const { data } = useZipCodeDeliverabilityCheck(zipCode, {
    enabled: disableDeliverabilityCheck === false && zipCode.length === 5,
  });

  useEffect(() => {
    if (!isLoading) {
      if (data) {
        setOnboardingZipCodePersistentData({
          zipCode,
          isDeliverable: data.isDeliverable,
          latitude: data.latitude,
          longitude: data.longitude,
        });
        if (data.isDeliverable) {
          trackZipCodeCheckSuccess({ zipcode: zipCode, pagepath: pathname });
        } else {
          trackZipCodeCheckFailed({ zipcode: zipCode, pagepath: pathname });
        }
      }
    }
  }, [
    data,
    setOnboardingZipCodePersistentData,
    isLoading,
    pathname,
    trackZipCodeCheckFailed,
    trackZipCodeCheckSuccess,
    values,
    name,
    zipCode,
  ]);

  return (
    <div className={wrapperClassName}>
      <StyledFormikTextField
        adornment={
          data?.isDeliverable
            ? {
                right: <StyledIcon iconString={CAPSULE_ICON_STRINGS.check_circle} />,
                size: 'md',
                style: { bottom: '0' },
              }
            : undefined
        }
        id={id || name}
        maxLength={5}
        type="text"
        name={name}
        defaultValue={defaultValue}
        label={{
          content: internalLabel,
          isHidden: false,
        }}
        onInput={e => {
          e.target.value = e.target.value.replace(/[^0-9]/g, '');
        }}
        onChange={e => {
          if (e.target.value === '' || (e.target.value.length < 6 && e.target.value.match(/^[0-9]+$/))) {
            if (e.target.value.length < 5) {
              setOnboardingZipCodePersistentData({
                zipCode,
                isDeliverable: undefined,
                latitude: undefined,
                longitude: undefined,
              });
            }
          }
        }}
        onFocus={e => {
          setInternalLabel(defaultLabelOnFocus);
          const { name } = e.target;
          trackFieldFocused({
            pagepath: pathname,
            fieldname: name,
          });
        }}
        onBlur={e => {
          if (e.target.value === '') {
            setInternalLabel(defaultLabel);
          }
        }}
        inputBottomPadding="0rem"
        labelSize="sm"
      />
      {data?.isDeliverable && zipCode.length === 5 ? (
        <StyledSuccessMessage>
          {successMessage ? successMessage : <>Good news! You’re in our delivery area.</>}
        </StyledSuccessMessage>
      ) : (
        <EmptyHeight />
      )}
    </div>
  );
};
